import DialogBasic from "@/components/dialog/Dialog";
import ShareLinkButton from "@/components/share-link-button/ShareLinkButton";
import UpgradeStrip from "@/components/obfuscation/UpgradeStrip";
import { UPGRADE_LOCATIONS } from "@/lib/tracking";
import { getDataGridNewDetail } from "@/controllers/news";
import useModalLink, { openModalLink } from "src/hooks/useModalLink";

/** Opens a news story in the app-wide reader, and puts its link (?show=news&id=) in the URL. */
export const openNewsStory = (id: string | number) => openModalLink("news", id);

/** The id a news row carries: `newsId` on stock movers and news search, `id` on news lists. */
export const newsIdOf = (row: { newsId?: string | number; id?: string | number }) => row?.newsId || row?.id;

/** A story, or a rejection: 4xx answers resolve (axios validateStatus < 500) with an error payload, not a story. */
const loadStory = async (id: string) => {
    const news = await getDataGridNewDetail(id);
    if (news?.obfuscated || news?.obfuscation?.obfuscated || news?.title || news?.articleHtml) return news;
    throw new Error("not a news story");
};

/**
 * The news story reader, mounted once in _app, so every news list and table opens the same
 * dialog and a story's link works on any page. Stories past the reader's plan show the way in
 * for that reader (UpgradeStrip) instead of the body.
 */
const NewsStoryModal = () => {
    const story = useModalLink<Record<string, any>>("news", loadStory, { global: true });
    const news = story.item;
    const locked = !!(news?.obfuscated || news?.obfuscation?.obfuscated);

    return (
        <DialogBasic
            isOpen={!!news}
            onClose={story.close}
            title={locked ? "News" : news?.title}
            source_fullname={locked ? "" : news?.sourceFullname}
            published_at={news?.publishedAt}
            description={locked ? "" : news?.description}
            content={locked ? undefined : news?.articleHtml}
            extraButtons={news && !locked && story.linked ? <ShareLinkButton title={news.title || "ORTEX news"} size="medium" /> : null}
            draggable
        >
            {locked ? <UpgradeStrip location={UPGRADE_LOCATIONS.NEWS_DETAIL} what="The full article is included with an ORTEX subscription." /> : undefined}
        </DialogBasic>
    );
};

export default NewsStoryModal;
